import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import {
  listActionItems,
  type ActionItem,
} from "@/lib/submissions/actionItems";

export type ActionItemsRead =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "done"; items: ActionItem[] };

/**
 * A submission's action items (PMS-S872), read when `enabled`, again on
 * `reload`, and again whenever `readAgainOn` changes (the quotes, read
 * again, so a review just asked for shows). A PMS before PMS-S872 (404)
 * reads as none. A quote's Actions tab (BRK-S465) and the submission's
 * (BRK-S464) both read through it.
 */
export function useActionItems(
  submissionId: string,
  enabled: boolean,
  readAgainOn?: unknown,
): {
  read: ActionItemsRead;
  /** Reads them again behind what's shown. */
  reload: () => void;
  /** After a failed read: reads them again, saying so. */
  retry: () => void;
} {
  const { authedRequest } = useAuth();
  const [read, setRead] = useState<ActionItemsRead>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    listActionItems(authedRequest, submissionId).then(
      (items) => {
        if (!cancelled) setRead({ kind: "done", items: items ?? [] });
      },
      () => {
        if (!cancelled) setRead({ kind: "failed" });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [authedRequest, submissionId, enabled, readAgainOn, attempt]);

  return {
    read,
    reload: () => setAttempt((n) => n + 1),
    retry: () => {
      setRead({ kind: "loading" });
      setAttempt((n) => n + 1);
    },
  };
}
